import { SITE_ALT_NAMES, SITE_NAME, SITE_SLOGAN, SITE_URL, SUPPORT, SUPPORT_PHONE_E164 } from '@/core/config/content';
import type { JsonLd } from './seo';

export const ORG_ID = `${SITE_URL}/#organization`;
export const SITE_ID = `${SITE_URL}/#website`;

export const organizationLd = (): JsonLd => ({
  '@type': 'Organization',
  '@id': ORG_ID,
  name: SITE_NAME,
  alternateName: [...SITE_ALT_NAMES],
  url: `${SITE_URL}/`,
  logo: { '@type': 'ImageObject', url: `${SITE_URL}/Icon-512.png`, width: 512, height: 512 },
  slogan: SITE_SLOGAN,
  description: 'بلینو (Belino) یک شرکت فناوری حمل‌ونقل بین‌شهری است که از سال ۱۴۰۳ با هدف دیجیتالی‌سازی عملیات شرکت‌ها و ایستگاه‌های حمل مسافر و بار فعالیت می‌کند. خدمات بلینو: خرید بلیط بین‌شهری، سفر درب‌به‌درب و رهگیری مرسوله.',
  email: SUPPORT.email,
  telephone: SUPPORT_PHONE_E164,
  address: { '@type': 'PostalAddress', addressLocality: 'کرمان', addressCountry: 'IR' },
  contactPoint: [{ '@type': 'ContactPoint', contactType: 'customer support', telephone: SUPPORT_PHONE_E164, email: SUPPORT.email, areaServed: 'IR', availableLanguage: ['fa'] }],
  sameAs: Object.values(SUPPORT.social),
});

export const websiteLd = (): JsonLd => ({
  '@type': 'WebSite',
  '@id': SITE_ID,
  url: `${SITE_URL}/`,
  name: SITE_NAME,
  alternateName: [...SITE_ALT_NAMES],
  inLanguage: 'fa-IR',
  publisher: { '@id': ORG_ID },
});

/** The three things Blino does, as schema.org Services owned by the Organization. */
export const servicesLd = (): JsonLd[] => [
  { '@type': 'Service', '@id': `${SITE_URL}/#service-intercity-ticket`, name: 'بلیط بین‌شهری', serviceType: 'Intercity taxi ticket booking', description: 'خرید آنلاین بلیط تاکسی بین‌شهری عادی و دربستی', provider: { '@id': ORG_ID }, areaServed: { '@type': 'Country', name: 'ایران' }, url: `${SITE_URL}/` },
  { '@type': 'Service', '@id': `${SITE_URL}/#service-door-to-door`, name: 'سفر درب‌به‌درب', serviceType: 'Door-to-door intercity ride', description: 'سفر اختصاصی بین‌شهری از درِ خانه تا مقصد', provider: { '@id': ORG_ID }, areaServed: { '@type': 'Country', name: 'ایران' } },
  { '@type': 'Service', '@id': `${SITE_URL}/#service-shipment-tracking`, name: 'رهگیری مرسوله', serviceType: 'Parcel and freight tracking', description: 'پیگیری لحظه‌ای وضعیت بار و مرسوله با کد رهگیری یا شماره موبایل', provider: { '@id': ORG_ID }, areaServed: { '@type': 'Country', name: 'ایران' }, url: `${SITE_URL}/track` },
];

export const breadcrumbLd = (items: { name: string; path: string }[]): JsonLd => ({
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({ '@type': 'ListItem', position: i + 1, name: it.name, item: `${SITE_URL}${it.path}` })),
});

/** A generic page node that points at the site + organization. */
export const webPageLd = (o: { type?: string; name: string; description: string; path: string; dateModified?: string }): JsonLd => ({
  '@type': o.type ?? 'WebPage',
  '@id': `${SITE_URL}${o.path}#webpage`,
  url: `${SITE_URL}${o.path}`,
  name: o.name,
  description: o.description,
  inLanguage: 'fa-IR',
  isPartOf: { '@id': SITE_ID },
  publisher: { '@id': ORG_ID },
  ...(o.dateModified ? { dateModified: o.dateModified } : {}),
});

export const graph = (...nodes: (JsonLd | JsonLd[])[]): JsonLd => ({ '@context': 'https://schema.org', '@graph': nodes.flat() });

/** Standard JSON-LD for a simple inner page: the page itself + Home › Page breadcrumbs. */
export const pageLd = (o: { type?: string; name: string; description: string; path: string; extra?: JsonLd }): JsonLd =>
  graph(
    { ...webPageLd(o), ...(o.extra ?? {}) },
    breadcrumbLd([
      { name: SITE_NAME, path: '/' },
      { name: o.name, path: o.path },
    ]),
  );
